import React from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Grid, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { cloneDeep as _cloneDeep, get as _get } from 'lodash';

import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { updateClaimInsureds } from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { getClaimCreationProductType, getPolicyInsureds } from '../../../app/store/combinedSelectors';
import { updatePolicyInsureds } from '../../../app/store/Policies/PoliciesSlice';
import { backBtn, cancelBtn, continueBtn } from '../../../Constants/Constants';
import { GenericProductType } from '../../../types/type';
import { FIRST_NAME_REGEX, LAST_NAME_REGEX } from '../../../utils/Regex-Util';
import CallToAction from '../../shared/CallToAction/CallToAction';
import { RegexTextField } from '../../shared/RegexTextField/RegexTextField';

export default function ConfirmInsureds() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const insureds = useSelector(getPolicyInsureds);
  const existingPolicyState = useAppSelector((state) => state.policies.policies);
  const typeOfClaim = useSelector(getClaimCreationProductType);

  const [localInsureds, setLocalInsureds] = React.useState(insureds);

  function handleFirstNameUpdate(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) {
    const updatedInsureds = [...localInsureds];
    updatedInsureds[index].firstName = _get(event, 'target.value', '');
    setLocalInsureds(updatedInsureds);
  }

  function handleLastNameUpdate(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) {
    const updatedInsureds = [...localInsureds];
    updatedInsureds[index].lastName = _get(event, 'target.value', '');
    setLocalInsureds(updatedInsureds);
  }

  function updatePolicyState(insureds: any[], policies: any[]) {
    insureds.forEach((insured: any) => {
      policies.forEach((policy: any) => {
        // Look at policy level policy roles
        _get(policy, 'policy.policyRoles', []).forEach((role: any) => {
          if (role.contactReferenceKey === insured.contactReferenceKey) {
            role.firstName = _get(insured, 'firstName', role.firstName);
            role.lastName = _get(insured, 'lastName', role.lastName);
          }
        });

        _get(policy, 'risks', []).forEach((risk: any) => {
          // Look at risk level policy roles

          _get(risk, 'policy.policyRoles', []).forEach((role: any) => {
            if (role.contactReferenceKey === insured.contactReferenceKey) {
              role.firstName = _get(insured, 'firstName', role.firstName);
              role.lastName = _get(insured, 'lastName', role.lastName);
            }
          });
          // Look at risk level vehicleRoles roles

          _get(risk, 'vehicleRoles', []).forEach((role: any) => {
            if (role.contactReferenceKey === insured.contactReferenceKey) {
              role.firstName = _get(insured, 'firstName', role.firstName);
              role.lastName = _get(insured, 'lastName', role.lastName);
            }
          });
        });
      });
    });
    return policies;
  }

  function handleNext() {
    localInsureds.forEach((insured: any) => {
      delete insured.id;
    });
    dispatch(updateClaimInsureds({ insureds: localInsureds }));
    const updatedPolicyState = updatePolicyState(localInsureds, _cloneDeep(existingPolicyState));
    dispatch(updatePolicyInsureds({ policies: updatedPolicyState }));

    if (typeOfClaim === GenericProductType.Auto) {
      navigate('/driver-selection');
    } else {
      navigate('/time-location-selection');
    }
  }

  function handleCancel() {
    navigate('/claims-overview');
  }

  function handleBack() {
    navigate('/risk-selection');
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        maxWidth: '1280px',
        minWidth: '310px',
        margin: '0 auto',
        marginTop: '1em',
      }}
    >
      <Grid item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          Confirm Name of Insureds
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography sx={{ marginTop: '1em' }}>
          We found the following named insured(s) for this policy. Please validate that the spelling of the names is
          correct. If both the first and last name show up in the last name field, please update that.
        </Typography>
      </Grid>

      <Grid sx={{ marginTop: '1em' }} item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          Primary Insured
        </Typography>
      </Grid>

      {localInsureds &&
        localInsureds
          .filter((insured: any) => !!insured.isPrimary)
          .map((insured: any, index: number) => (
            <Grid container>
              <Grid
                sx={{
                  marginTop: '1em',
                  marginBottom: '1em',
                  paddingLeft: '1%',
                }}
                item
                xs={6}
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Typography sx={theme.custom.styles.formLabelStyle}>First Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="firstname"
                      value={insured.firstName}
                      regex={FIRST_NAME_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleFirstNameUpdate(event, index)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '99%', paddingLeft: '1%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                sx={{
                  marginTop: '1em',
                  marginBottom: '1em',
                  paddingLeft: '1%',
                }}
                item
                xs={6}
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Typography sx={theme.custom.styles.formLabelStyle}>Last Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="lastname"
                      value={insured.lastName}
                      regex={LAST_NAME_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleLastNameUpdate(event, index)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '99%', paddingLeft: '1%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}

      {localInsureds && localInsureds.filter((insured: any) => !insured.isPrimary).length > 0 && (
        <Grid item xs={12}>
          <Typography
            sx={{
              ...theme.custom.styles.titleType4,
            }}
          >
            Secondary Insured(s)
          </Typography>
        </Grid>
      )}

      {localInsureds &&
        localInsureds
          .filter((insured: any) => !insured.isPrimary)
          .map((insured: any, index: number) => (
            <Grid container>
              <Grid
                sx={{
                  marginTop: '1em',
                  marginBottom: '1em',
                  paddingLeft: '1%',
                }}
                item
                xs={6}
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Typography sx={theme.custom.styles.formLabelStyle}>First Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="firstname"
                      value={insured.firstName}
                      regex={FIRST_NAME_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleFirstNameUpdate(event, index)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '99%', paddingLeft: '1%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                sx={{
                  marginTop: '1em',
                  marginBottom: '1em',
                  paddingLeft: '1%',
                }}
                item
                xs={6}
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Typography sx={theme.custom.styles.formLabelStyle}>Last Name</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RegexTextField
                      id="lastname"
                      value={insured.lastName}
                      regex={LAST_NAME_REGEX}
                      changeHandler={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleLastNameUpdate(event, index)
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                      sx={{ width: '99%', paddingLeft: '1%' }}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}

      <Grid item xs={12}>
        <CallToAction
          nextButton={true}
          nextButtonDisabled={false}
          nextButtonContent={continueBtn}
          handleNext={handleNext}
          backButton={true}
          backButtonContent={backBtn}
          handleBack={handleBack}
          skipButton={false}
          cancelButton={true}
          cancelButtonContent={cancelBtn}
          handleCancel={handleCancel}
        ></CallToAction>
      </Grid>
    </Grid>
  );
}
